import { createApi } from "@reduxjs/toolkit/query/react";
import queryString from "query-string";
import useAxios from "axios-hooks";
import { useErrorAlert, useSuccessAlert } from "hooks";
import { useEffect, useMemo, useState } from "react";
import { ExportQueueItem, TemplateAttribute } from "types/sku-bulk-update";
import { axiosBaseQuery } from "../axios-base-query";
import { File } from "../../types/global";

export const bulkUpdateApi = createApi({
  reducerPath: "bulkUpdateApi",
  baseQuery: axiosBaseQuery({
    baseUrl: `${process.env.REACT_APP_API_URL}/buyer`,
  }),
  tagTypes: ["getReportTemplates", "getReportTemplateAttributes"],
  endpoints: (builder) => ({
    getReportTemplateAttributes: builder.query<
      TemplateAttribute[],
      { templateId: number | string; includedItem?: "true" | "false" | "" } | null
    >({
      query: (payload) => {
        const query = queryString.stringify(
          { includedItem: payload?.includedItem || "false" },
          { skipEmptyString: true, skipNull: true },
        );

        return {
          url: `/templates/${payload?.templateId}/attributes${query ? `?${query}` : ""}`,
          method: "GET",
        };
      },
      providesTags: ["getReportTemplateAttributes"],
    }),
    // downloadCustomizedTemplate: builder.mutation<ExportQueueItem | string, { payload: any }>({
    //   query: (payload) => ({
    //     url: "/daily_po/bulk_update/download_template",
    //     method: "POST",
    //     body: payload,
    //     successAlertEnabled: true,
    //     successMessage: "Your template has been downloaded",
    //   }),
    // }),
    exportTemplate: builder.mutation<ExportQueueItem | string, { payload: any }>({
      query: (payload) => ({
        url: "/submit_action_endpoint_here",
        method: "POST",
        body: payload,
        successAlertEnabled: true,
        successMessage: "Your template has been dowmloaded",
        // successAlertEnabled: true,
        // successMessage: "OK",
      }),
    }),
  }),
});

export function useDownloadCustomizedTemplate() {
  const { setError } = useErrorAlert();

  const [{ data: fetchData, loading: isLoading, error }, refetch] = useAxios(
    {
      method: "POST",
      withCredentials: true,
      responseType: "blob",
    },
    {
      manual: true,
    },
  );

  useEffect(() => {
    if (error) setError(error);
  }, [error]);

  return useMemo(
    () => ({
      data: fetchData?.data,
      isLoading,
      refetch: (payload: any) =>
        refetch({ url: "/buyer/daily_po/bulk_update/download_template", data: payload }),
      error,
    }),
    [fetchData, isLoading],
  );
}

export function useUploadBulkUpdate() {
  const { setError } = useErrorAlert();
  const { setSuccess } = useSuccessAlert();

  const [file, setFile] = useState<File>();

  const [{ data: fetchData, loading: isLoading, error }, refetch] = useAxios(
    {
      url: "buyer/daily_po/bulk_update/upload",
      method: "POST",
      headers: {
        "Content-Type": "multipart/form-data",
      },
      responseType: "blob",
    },
    {
      manual: true,
      autoCancel: false, // avoid cancel request when uploading multiple files
    },
  );

  useEffect(() => {
    if (error) setError(error);
  }, [error]);

  useEffect(() => {
    if (fetchData?.data?.code === 200) setSuccess("File has been uploaded successfully.");

    if (fetchData?.data) setFile(fetchData?.data);
  }, [fetchData]);

  return useMemo(
    () => ({
      data: file,
      isLoading,
      refetch: (payload: FormData) => refetch({ data: payload }),
      error,
    }),
    [fetchData, isLoading],
  );
}

export const {
  useLazyGetReportTemplateAttributesQuery,
  useExportTemplateMutation,
  // useDownloadCustomizedTemplateMutation,
} = bulkUpdateApi;
